/**
 * ReportIssue - "Report an issue"
 * --------------------------------
 * One conversation, always open, between this person and whoever is
 * moderating. It is for the SITE being broken - a button that does nothing, a
 * page that will not load, a photo that will not upload - not for reporting a
 * person, which is the report button on their profile and goes somewhere
 * quite different.
 *
 * WHY EVERY REPLY SAYS "MODERATOR" AND CANNOT BE CLICKED.
 *
 * The moderator is also an ordinary member with a name, a photograph and a
 * profile page. A student who reports a bug on Tuesday should not find
 * themselves recognising that person on the marketplace on Wednesday and
 * wondering whether the complaint followed them. The role answers, not the
 * individual.
 *
 * This screen could not leak the name even if it were written carelessly: the
 * server never sends it. See backend/models/SupportMessage.js.
 *
 * The layout deliberately mirrors MessageThread.jsx - same bubbles, same
 * composer, same scroll behaviour - because somebody who has used one should
 * not have to learn the other.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LifeBuoy, Send, ShieldCheck } from 'lucide-react';

import { supportApi } from '../services/api';
import { useI18n } from '../hooks/useI18n';
import { timeAgo } from '../utils/format';
import BackLink from '../components/BackLink';
import Loader from '../components/Loader';

export default function ReportIssue() {
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottom = useRef(null);
  const box = useRef(null);

  const load = useCallback(async (markRead = false) => {
    try {
      const data = await supportApi.thread();
      setMessages(data.messages || []);
      if (markRead) await supportApi.markRead();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  /**
   * Keep the conversation live while it is open, the same way the job thread
   * does. Somebody who has just reported that the site is broken is somebody
   * sitting there waiting for an answer, and asking them to refresh a page to
   * find one is a poor look on a screen about things not working.
   */
  useEffect(() => {
    const timer = setInterval(() => load(true), 15000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  /**
   * Grow the box to fit what is being typed, up to six lines - the same
   * behaviour as the job conversation, and for the same reason: a fixed box
   * is too tall for the one line most people send and too short the moment
   * somebody describes what went wrong properly.
   */
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight) || 22;
    const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    const frame = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + borders;

    el.style.height = 'auto';
    const natural = el.scrollHeight + borders;
    el.style.height = `${Math.min(natural, lineHeight * 6 + frame)}px`;
  }, [draft]);

  const send = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;

    setSending(true);
    setError('');
    try {
      const { message } = await supportApi.send(body);
      setMessages((current) => [...current, message]);
      setDraft('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Loader label={t('common.loading')} />;

  return (
    <div className="container section" style={{ maxWidth: 720 }}>
      <BackLink to="/dashboard" label={t('common.back')} />

      <div className="page-header">
        <span className="feature-icon">
          <LifeBuoy size={26} aria-hidden="true" />
        </span>
        <h1>{t('reportIssue.title')}</h1>
        <p>{t('reportIssue.subtitle')}</p>
      </div>

      {/* Where to go for the other kind of problem. Somebody frightened by a
          person should not be typing it into a bug report and waiting - this
          says so before they start rather than after. */}
      <div className="alert alert-info" style={{ marginBottom: 'var(--space-md)' }}>
        <ShieldCheck size={18} aria-hidden="true" />
        <div className="text-sm">{t('reportIssue.notForPeople')}</div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--space-md)' }}>
          {error}
        </div>
      )}

      {/* Same bubbles and composer as the job conversation - the classes are
          shared on purpose, so somebody who has used one screen already knows
          this one. */}
      <div className="card">
        {messages.length === 0 ? (
          <p className="chat-empty">{t('reportIssue.empty')}</p>
        ) : (
          messages.map((message) => (
            <div
              key={message._id}
              className={`message-row${message.mine ? ' is-mine' : ''}`}
            >
              <div className="message-bubble">
                {/* Plain text, not a link, and no avatar. There is nobody to
                    go and look at - that is the point, not an omission. */}
                {!message.mine && (
                  <strong className="text-sm">{t('reportIssue.moderator')}</strong>
                )}
                <p>{message.body}</p>
                <span className="message-time">{timeAgo(message.createdAt)}</span>
              </div>
            </div>
          ))
        )}
        <div ref={bottom} />
      </div>

      {/* The SAME markup as MessageThread.jsx, deliberately - one line of
          textarea and the round icon button, not a full-width labelled one.
          The first version used `btn btn-primary` with the word "Send" on it,
          which on a phone put a button wider than the box you type into next
          to a box two rows tall. Somebody who has used the job conversation
          should not find this one laid out differently. */}
      <form onSubmit={send} className="chat-compose" style={{ borderTop: 'none' }}>
        <div className="chat-compose-row">
          <label className="sr-only" htmlFor="issue-body">{t('reportIssue.yourMessage')}</label>
          <textarea
            id="issue-body"
            ref={box}
            rows={1}
            value={draft}
            maxLength={4000}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('reportIssue.placeholder')}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter makes a new line - the same as the
              // job conversation. On a phone the return key inserts a newline
              // as usual, because there is no shift to hold, which is why the
              // button is there too.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send(event);
              }
            }}
          />
          <button
            type="submit"
            className="chat-send"
            disabled={sending || !draft.trim()}
            aria-label={t('reportIssue.send')}
          >
            <Send size={18} aria-hidden="true" />
          </button>
        </div>
      </form>
    </div>
  );
}
